import React, { useState } from 'react';
import { List, Checkbox, Select, Input, Button, message, Typography, Space, Popconfirm, Modal, Upload, Alert, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, LinkOutlined } from '@ant-design/icons';
import { TasksService } from '../api/services/tasks.service';
import { useAuth } from '../store/auth';
import { Link } from 'react-router-dom';
import LinkifyText from './LinkifyText';

const { Text } = Typography;

export default function TaskChecklist({ task, users, onUpdate }) {
  const [newItemText, setNewItemText] = useState('');
  const [newItemAssignee, setNewItemAssignee] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Checklist result modal
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [resultComment, setResultComment] = useState('');
  const [resultFile, setResultFile] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);

  const { user: currentUser } = useAuth();
  
  const isSuperAdmin = currentUser && currentUser.role_level >= 5;
  const isCreatorOrCuratorOrAssignee = currentUser && (
    currentUser.id == task.initiator_id || 
    currentUser.id == task.curator_id || 
    currentUser.id == task.assignee_id ||
    currentUser.id == task.creator_id ||
    isSuperAdmin
  );

  const userOptions = users.map(u => ({ value: u.id, label: u.full_name || u.email }));

  const handleAdd = async () => {
    if (!newItemText.trim()) return;
    setLoading(true);
    try {
      await TasksService.addChecklistItem(task.id, {
        text: newItemText,
        assignee_id: newItemAssignee
      });
      message.success('Пункт добавлен');
      setNewItemText('');
      setNewItemAssignee(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      message.error('Ошибка добавления');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (item, checked) => {
    if (checked) {
       setCurrentItem(item);
       setResultComment('');
       setResultFile(null);
       setResultModalOpen(true);
    } else {
       // Allow unchecking without comment
       submitToggle(item, false, '', null);
    }
  };
  
  const submitToggle = async (item, checked, comment, fileId) => {
    try {
      await TasksService.updateChecklistItem(task.id, item.id, {
        is_completed: checked,
        result_comment: comment,
        result_file_id: fileId
      });
      if (onUpdate) onUpdate();
      return true;
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка обновления');
      return false;
    }
  };
  
  const handleResultSubmit = async () => {
     if (!resultComment.trim()) {
        message.warning('Введите комментарий к результату выполнения');
        return;
     }
     
     setResultLoading(true);
     let finalFileId = null;
     
     try {
        if (resultFile) {
           const formData = new FormData();
           formData.append('file', resultFile);
           const uploadRes = await TasksService.addAttachment(task.id, formData);
           if (uploadRes.data && uploadRes.data.file_id) {
               finalFileId = uploadRes.data.file_id;
           }
        }
        
        const success = await submitToggle(currentItem, true, resultComment, finalFileId);
        if (success) {
           setResultModalOpen(false);
           setCurrentItem(null);
           message.success('Результат сохранен');
        }
     } catch (err) {
        message.error('Ошибка при сохранении результата');
     } finally {
        setResultLoading(false);
     }
  };

  const handleAssign = async (item, assigneeId) => {
    try {
      await TasksService.updateChecklistItem(task.id, item.id, {
        assignee_id: assigneeId
      });
      message.success('Исполнитель назначен');
      if (onUpdate) onUpdate();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка назначения');
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await TasksService.deleteChecklistItem(task.id, itemId);
      message.success('Пункт удален');
      if (onUpdate) onUpdate();
    } catch (e) {
      message.error('Ошибка удаления');
    }
  };

  const hasEditRights = (item) => {
    return isSuperAdmin || (currentUser && (
      currentUser.id == task.initiator_id || 
      currentUser.id == task.curator_id || 
      currentUser.id == task.assignee_id ||
      currentUser.id == task.creator_id ||
      currentUser.id == item.assignee_id
    ));
  };

  return (
    <div style={{ marginTop: 24, marginBottom: 24 }}>
      <h4>Чек-лист задачи</h4>
      <List
        size="small"
        dataSource={task.checklists || []}
        renderItem={item => {
          const canEdit = hasEditRights(item);
          return (
            <List.Item
              actions={
                isCreatorOrCuratorOrAssignee ? [
                  <Popconfirm title="Удалить пункт?" onConfirm={() => handleDelete(item.id)}>
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                  </Popconfirm>
                ] : []
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '10px' }}>
                    <Checkbox 
                      checked={item.is_completed} 
                      disabled={!canEdit}
                      onChange={(e) => handleToggle(item, e.target.checked)}
                    />
                    <div style={{ flex: 1 }}>
                        <Text delete={item.is_completed}><LinkifyText text={item.text} /></Text>
                        {item.process_instance_id && (
                           <Tag color="blue" icon={<LinkOutlined />} style={{ marginLeft: 8 }}>
                             <Link to="/bpm" state={{ openProcessId: item.process_instance_id }}>Процесс #{item.process_instance_id}</Link>
                           </Tag>
                        )}
                    </div>
                    
                    <Select
                      size="small"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Исполнитель"
                      style={{ width: 200 }}
                      value={item.assignee_id}
                      options={userOptions}
                      disabled={!isCreatorOrCuratorOrAssignee}
                      onChange={(val) => handleAssign(item, val)}
                    />
                  </div>
                  {item.is_completed && item.result_comment && (
                      <div style={{ marginLeft: 26, marginTop: 4, padding: 8, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Результат:</Text><br/>
                          <Text>{item.result_comment}</Text>
                          {item.result_file_id && (
                             <div style={{ marginTop: 4 }}>
                                <a href={`/api/v1/files/${item.result_file_id}/nc-url`} target="_blank" rel="noopener noreferrer">
                                   Вложенный файл
                                </a>
                             </div>
                          )}
                      </div>
                  )}
              </div>
            </List.Item>
          );
        }}
      />
      {isCreatorOrCuratorOrAssignee && (
        <div style={{ display: 'flex', gap: '10px', marginTop: 10 }}>
          <Input 
            placeholder="Новый пункт чек-листа..." 
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onPressEnter={handleAdd}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Исполнитель"
            style={{ width: 200 }}
            value={newItemAssignee}
            onChange={setNewItemAssignee}
            options={userOptions}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} loading={loading}>
            Добавить
          </Button>
        </div>
      )}
      
      <Modal
         title="Отметка о выполнении"
         open={resultModalOpen}
         onCancel={() => {
            setResultModalOpen(false);
            setCurrentItem(null);
         }}
         onOk={handleResultSubmit}
         confirmLoading={resultLoading}
      >
         <Space direction="vertical" style={{ width: '100%' }}>
            <Alert message="Для завершения пункта необходимо оставить комментарий с результатом." type="info" />
            <div>
               <Text strong>Комментарий (обязательно):</Text>
               <Input.TextArea 
                  rows={4} 
                  value={resultComment} 
                  onChange={e => setResultComment(e.target.value)} 
                  placeholder="Опишите результат выполнения..."
               />
            </div>
            <div>
               <Text strong>Прикрепить файл (опционально):</Text><br/>
               <Upload
                  beforeUpload={(file) => {
                     setResultFile(file);
                     return false;
                  }}
                  fileList={resultFile ? [{ uid: '-1', name: resultFile.name, status: 'done' }] : []}
                  onRemove={() => setResultFile(null)}
                  maxCount={1}
               >
                  <Button icon={<UploadOutlined />}>Выбрать файл</Button>
               </Upload>
            </div>
         </Space>
      </Modal>
    </div>
  );
}
